import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, ChevronDown } from "lucide-react";
import { API } from "../utils/api";

const LOCATION = "4324 N High St, Columbus, OH 43214";

// ── Pricing block — shared between popup header and inline header ──────────────
const PricingBlock = () => (
  <div className="mb-4 space-y-2">
    <div className="bg-[#FFB612]/10 border border-[#FFB612]/40 px-4 py-3">
      <p className="text-[#FFB612] font-black text-2xl uppercase tracking-wide leading-tight">
        Just $19.99/Week!
      </p>
      <p className="text-white font-bold text-sm mt-1">
        Only $19.99 to get started with the challenge!
      </p>
    </div>
    <p className="text-white text-sm leading-relaxed">
      Meet Ron K and learn how it works. Orientation is{" "}
      <span className="text-[#FFB612] font-bold">FREE</span> — no payment or
      obligation required.
    </p>
    <p className="text-white text-xs">
      Challenge: 10 weekly payments totaling $199.90.
    </p>
  </div>
);

// ── Booking form (shared between popup and inline) ────────────────────────────
const BookingForm = ({ formData, setFormData, onSubmit, loading, dates }) => {
  const selectedDateData = dates.find((d) => d.date === formData.date);
  const availableTimes = selectedDateData ? selectedDateData.slots : [];

  const handleDateChange = (e) => {
    setFormData((f) => ({ ...f, date: e.target.value, time: "" }));
  };

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4"
      data-testid="orientation-booking-form"
    >
      {/* Gym address */}
      <p className="text-[#FFB612] text-sm font-bold mb-1">
        📍 {LOCATION}
      </p>

      {/* First Name */}
      <div>
        <label className="block text-sm font-bold text-white mb-1">
          First Name <span className="text-[#FFB612]">*</span>
        </label>
        <input
          type="text"
          value={formData.firstName}
          onChange={(e) => setFormData((f) => ({ ...f, firstName: e.target.value }))}
          placeholder="Your first name"
          required
          className="w-full bg-[#111] border border-white/20 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#FFB612] placeholder-white"
          data-testid="booking-first-name"
        />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm font-bold text-white mb-1">
          Phone Number <span className="text-[#FFB612]">*</span>
        </label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))}
          placeholder="(614) 000-0000"
          required
          className="w-full bg-[#111] border border-white/20 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#FFB612] placeholder-white"
          data-testid="booking-phone"
        />
      </div>

      {/* Select Day */}
      <div>
        <label className="block text-sm font-bold text-white mb-1">
          Select Day <span className="text-[#FFB612]">*</span>
        </label>
        <div className="relative">
          <select
            value={formData.date}
            onChange={handleDateChange}
            required
            className="w-full bg-[#111] border border-white/20 text-white px-4 py-3 text-sm appearance-none focus:outline-none focus:border-[#FFB612] pr-10"
            data-testid="booking-date"
          >
            <option value="">— Choose a day —</option>
            {dates.map((d) => (
              <option key={d.date} value={d.date}>
                {d.display}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        </div>
      </div>

      {/* Select Time */}
      <div>
        <label className="block text-sm font-bold text-white mb-1">
          Select Time <span className="text-[#FFB612]">*</span>
        </label>
        <div className="relative">
          <select
            value={formData.time}
            onChange={(e) => setFormData((f) => ({ ...f, time: e.target.value }))}
            required
            disabled={!formData.date}
            className="w-full bg-[#111] border border-white/20 text-white px-4 py-3 text-sm appearance-none focus:outline-none focus:border-[#FFB612] disabled:border-white/10 disabled:cursor-not-allowed pr-10"
            data-testid="booking-time"
          >
            <option value="">— {formData.date ? "Choose a time" : "Select a day first"} —</option>
            {availableTimes.map((s) => (
              <option key={s.time} value={s.time}>
                {s.time}
                {s.available <= 3 ? ` (${s.available} spot${s.available !== 1 ? "s" : ""} left)` : ""}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        </div>
      </div>

      {/* Consent note */}
      <p className="text-[11px] text-white leading-relaxed">
        By confirming you agree to receive a one-time SMS confirmation. Msg &amp; data rates
        may apply. No spam — just your orientation details.
      </p>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-4 bg-[#FFB612] text-black font-black text-sm uppercase tracking-widest hover:bg-[#FFB612]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="booking-submit"
      >
        {loading ? "Booking…" : "Confirm Booking"}
      </button>
    </form>
  );
};

// ── Confirmation view ─────────────────────────────────────────────────────────
const Confirmation = ({ booking }) => (
  <div className="text-center space-y-5" data-testid="booking-confirmation">
    <div className="text-5xl">🥊</div>
    <h2 className="text-2xl font-black uppercase text-[#FFB612]">You're Booked!</h2>
    <p className="text-white font-bold">
      Your FREE 30-Class Challenge orientation is confirmed!
    </p>
    <div className="bg-[#111] border border-[#FFB612]/30 p-5 space-y-2 text-left text-sm">
      <p>
        <span className="text-white">Date:</span>{" "}
        <span className="text-white font-bold">{booking.display_date}</span>
      </p>
      <p>
        <span className="text-white">Time:</span>{" "}
        <span className="text-white font-bold">{booking.slot_time}</span>
      </p>
      <p>
        <span className="text-white">Location:</span>{" "}
        <span className="text-white font-bold">{LOCATION}</span>
      </p>
    </div>
    <p className="text-white text-sm">You'll receive a confirmation text shortly!</p>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
export default function BookingPage() {
  const [dates, setDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(true);
  const [bookingsEnabled, setBookingsEnabled] = useState(true);

  // Shared form state — both popup and inline write to the same object
  const [formData, setFormData] = useState({ firstName: "", phone: "", date: "", time: "" });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  // Popup — initialized synchronously from sessionStorage so it shows on the
  // very first render with zero delay. The flag is set here too so a reload
  // during the same session doesn't re-open it.
  const [showPopup, setShowPopup] = useState(() => {
    if (sessionStorage.getItem("orientation_popup_seen")) return false;
    sessionStorage.setItem("orientation_popup_seen", "1");
    return true;
  });

  // Add noindex meta tag so this page is never indexed
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = "Book Orientation | Neighborhood Boxing Club";
    return () => { document.head.removeChild(meta); };
  }, []);

  // Lock body scroll when popup is open — prevents the webpage behind the popup
  // from scrolling on mobile (Chrome, Facebook, Instagram in-app browsers).
  // The cleanup always restores normal scroll when the popup closes or unmounts.
  useEffect(() => {
    if (showPopup && !confirmation) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [showPopup, confirmation]);

  // Load available slots
  useEffect(() => {
    axios
      .get(`${API}/orientation/available-slots`)
      .then((r) => {
        setBookingsEnabled(r.data.enabled !== false);
        setDates(r.data.dates || []);
      })
      .catch(() => setDates([]))
      .finally(() => setDatesLoading(false));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      setSubmitLoading(true);
      try {
        const { data } = await axios.post(`${API}/orientation/bookings`, {
          first_name: formData.firstName,
          phone: formData.phone,
          slot_date: formData.date,
          slot_time: formData.time,
        });
        setConfirmation(data);
        // ── Pixel tracking: fires ONCE on confirmed orientation booking ─────
        // Guard: inside the try block, after a successful POST response only.
        // Never fires on form open, click, validation error, or failed booking.
        if (window.fbq) {
          window.fbq("track", "Lead", {
            content_name: "30-Class Challenge Orientation",
            content_category: "Orientation Booking",
          });
          window.fbq("track", "Schedule", {
            content_name: "30-Class Challenge Orientation",
            content_category: "Orientation Booking",
          });
        }
        if (window.gtag) {
          window.gtag("event", "generate_lead", {
            value: 0,
            currency: "USD",
            lead_type: "orientation_booking",
          });
        }
        setShowPopup(false);
        // Refresh slots so the booked slot updates its count
        const r = await axios.get(`${API}/orientation/available-slots`);
        setDates(r.data.dates || []);
      } catch (err) {
        setError(
          err.response?.data?.detail ||
            "Something went wrong. Please try again."
        );
      } finally {
        setSubmitLoading(false);
      }
    },
    [formData]
  );

  const closePopup = () => setShowPopup(false);

  return (
    <div className="min-h-screen bg-[#050505] text-white">

      {/* ── Popup ── */}
      {showPopup && !confirmation && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-hidden"
          data-testid="booking-popup"
        >
          {/*
            Modal: flex-column layout so the X button row sits OUTSIDE the
            scroll area and is always visible regardless of scroll position.
            max-h uses calc(100vh - 2rem) which works across Chrome, Facebook,
            and Instagram in-app browsers. The inner scroll div uses
            -webkit-overflow-scrolling:touch for smooth momentum scrolling on iOS
            and overscroll-contain to prevent the page behind from scrolling.
          */}
          <div
            className="bg-[#0A0A0A] border border-white/10 w-full max-w-md flex flex-col"
            style={{ maxHeight: "calc(100vh - 2rem)" }}
          >
            {/* Always-visible close button — outside the scroll area */}
            <div className="flex justify-end px-4 pt-4 pb-2 flex-shrink-0">
              <button
                onClick={closePopup}
                className="text-white/40 hover:text-white transition-colors"
                aria-label="Close popup"
                data-testid="close-popup"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable content — full form including pricing, fields, and button */}
            <div
              className="overflow-y-auto overscroll-contain flex-1 px-6 pb-6"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <PageHeader compact />
              {error && (
                <p className="text-red-400 text-sm mb-3 bg-red-500/10 px-3 py-2">{error}</p>
              )}
              {datesLoading ? (
                <p className="text-white text-sm py-6 text-center">Loading availability…</p>
              ) : !bookingsEnabled ? (
                <DisabledNotice />
              ) : (
                <BookingForm
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleSubmit}
                  loading={submitLoading}
                  dates={dates}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Inline page ── */}
      <div className="max-w-md mx-auto px-4 py-10">
        {confirmation ? (
          <Confirmation booking={confirmation} />
        ) : (
          <>
            <PageHeader />
            {error && (
              <p className="text-red-400 text-sm mb-4 bg-red-500/10 px-3 py-2">{error}</p>
            )}
            {datesLoading ? (
              <p className="text-white text-sm py-6 text-center">Loading availability…</p>
            ) : !bookingsEnabled ? (
              <DisabledNotice />
            ) : (
              <BookingForm
                formData={formData}
                setFormData={setFormData}
                onSubmit={handleSubmit}
                loading={submitLoading}
                dates={dates}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────
const PageHeader = ({ compact = false }) => (
  <div className={compact ? "mb-4" : "mb-6"}>
    {/* Logo — shown on the full page; hidden in the compact popup to save space */}
    {!compact && (
      <div className="flex items-center gap-2 mb-3">
        <img src="/nbc_logo.jpg" alt="NBC" className="h-8" />
      </div>
    )}

    {/* Title block */}
    <h1 className={`font-black uppercase text-[#FFB612] leading-tight mb-1 ${compact ? "text-xl" : "text-3xl"}`}>
      🥊 30-Class Challenge
    </h1>
    <h2 className={`font-black uppercase text-white mb-3 ${compact ? "text-base" : "text-xl"}`}>
      Book Your Free Orientation
    </h2>

    {/* Pricing block */}
    <PricingBlock />
  </div>
);

const DisabledNotice = () => (
  <div className="bg-[#111] border border-white/10 p-6 text-center space-y-2">
    <p className="text-white text-sm">Online booking is temporarily unavailable.</p>
    <p className="text-white text-xs">Please check back soon or call us directly.</p>
  </div>
);
